import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import SignoutHeader from "../subcomponents/SignoutHeader";
import { Colors } from "../Colors";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../store";
import { getWaitlistPatients } from "../slices/PatientSlice";
import axios from "axios";
import { AppointmentType, CompleteActivePlanOfCare, EHR, Insurance, ProviderGroup, QueuePatient, WebptPlanOfCareMetadata } from "../types";
import { PlanOfCareActivePreview } from "./PatientPlanOfCarePage";
import { CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, Tab, Typography } from "@mui/material";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { SecondaryButton } from "../subcomponents/CustomButton";
import { useEffect, useRef } from "react";
import LoadingWithMessage from "../subcomponents/LoadingWithMessage";
import { useSnackbar } from "../providers/SnackbarProvider";
import { api } from "../utils/utils";
import { Link, useNavigate } from "react-router-dom";
import { DateTime } from "luxon";
import { ArrowOutwardOutlined, FileDownloadOutlined, RefreshOutlined as RefreshIcon } from "@mui/icons-material";
import { Parser } from "@json2csv/plainjs";

const findAppointmentTypeKeywordMatch = (caseStudyName: string, appointmentTypes: AppointmentType[]): { keyword: string; appointmentType: AppointmentType } | null => {
  const caseStudyLower = caseStudyName.toLowerCase();
  for (const at of appointmentTypes) {
    if (!at.keyword) continue;
    const keywords = Array.isArray(at.keyword) ? at.keyword : [at.keyword];
    for (const kw of keywords) {
      if (caseStudyLower.includes(kw.toLowerCase())) {
        return { keyword: kw, appointmentType: at };
      }
    }
  }
  return null;
};

const findProviderGroupKeywordMatch = (caseStudyName: string, providerGroups: ProviderGroup[]): { keyword: string; providerGroup: ProviderGroup } | null => {
  const caseStudyLower = caseStudyName.toLowerCase();
  for (const pg of providerGroups) {
    if (!pg.keyword) continue;
    const keywords = Array.isArray(pg.keyword) ? pg.keyword : [pg.keyword];
    for (const kw of keywords) {
      if (caseStudyLower.includes(kw.toLowerCase())) {
        return { keyword: kw, providerGroup: pg };
      }
    }
  }
  return null;
};

type AutoAddRowProps = {
  patient: {
    id: number;
    name: string;
    caseStudyName: string;
    insuranceName: string;
    loading: boolean;
    numWeeks: number;
    numPerWeek: number;
    numVisits: number;
    rawFrequency: string;
    authorizedVisits: number;
    usedAuthorizedVisits: number;
    expirationDate: string;
    remainingAuthorizedVisits: number;
    remainingPlanOfCareVisits: number;
    totalPlanOfCareVisits: number;
    numBookable: number;
    potentialRevenue: number;
    ignoreAuthorization: boolean;
  };
  fetchPlanOfCare: (id: number) => void;
  planOfCare: CompleteActivePlanOfCare | null;
  appointmentTypes: AppointmentType[];
  providerGroups: ProviderGroup[];
  planOfCareLoading: boolean;
  onViewPlanOfCare: (patientId: number) => void;
};

const AutoAddRow: React.FC<AutoAddRowProps> = ({ patient, fetchPlanOfCare, planOfCare, appointmentTypes, providerGroups, planOfCareLoading, onViewPlanOfCare }) => {
  const user = useSelector((state: RootState) => state.auth.user);
  const { showMessage } = useSnackbar();
  const [adding, setAdding] = React.useState(false);
  const [added, setAdded] = React.useState(false);

  const keywordMatch = findAppointmentTypeKeywordMatch(patient.caseStudyName, appointmentTypes);
  const providerGroupKeywordMatch = findProviderGroupKeywordMatch(patient.caseStudyName, providerGroups);

  const authorizationExpiring = patient.authorizedVisits > patient.usedAuthorizedVisits + patient.remainingAuthorizedVisits;
  const authorizationExpired = DateTime.fromFormat(patient.expirationDate, "yyyy-MM-dd") < DateTime.local();

  const quickAddPatient = async (patientId: number) => {
    if (!user?.token) return;
    setAdding(true);
    try {
      const response = await api.post(`/patients/webpt/quick-add`, user.token, {
        webptPatientId: patientId,
      });

      console.log("Quick added patient:", response.data);
      setAdded(true);
      showMessage(`${patient.name} added successfully`, "success");
    } catch (error) {
      console.error("Error quick adding patient:", error);
      showMessage("Error adding patient", "error");
    } finally {
      setAdding(false);
    }
  };

  return (
    <TableRow key={`TableRow-${patient.id}`}>
      <TableCell>
        {added ? <span style={{ color: Colors.success }}>Added to Queue</span> : adding ? <CircularProgress size={24} /> : <Button onClick={() => quickAddPatient(patient.id)}>Add</Button>}
      </TableCell>
      <TableCell>
        <Box
          component="a"
          href={`https://app.webpt.com/patient/edit/index/PatientID/${patient.id}`}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
            color: Colors.primary,
            textDecoration: "none",
            fontWeight: 500,
            fontSize: "0.875rem",
            transition: "color 0.2s ease",
            "&:hover": {
              color: Colors.primaryDark,
            },
          }}
        >
          {patient.name} ({patient.id})
          <ArrowOutwardOutlined sx={{ fontSize: "14px", opacity: 0.7 }} />
        </Box>
      </TableCell>
      <TableCell>
        {patient.loading ? (
          <Typography variant="body2" color="text.secondary">
            Loading...
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
            <Typography variant="body2" color="text.secondary">
              {(() => {
                const name = patient.caseStudyName;
                const lower = name.toLowerCase();
                const apptIdx = keywordMatch ? lower.indexOf(keywordMatch.keyword.toLowerCase()) : -1;
                const pgIdx = providerGroupKeywordMatch ? lower.indexOf(providerGroupKeywordMatch.keyword.toLowerCase()) : -1;
                if (apptIdx === -1 && pgIdx === -1) return name;
                // Use whichever match appears first; prefer providerGroup if tied
                const useAppt = apptIdx !== -1 && (pgIdx === -1 || apptIdx < pgIdx);
                const idx = useAppt ? apptIdx : pgIdx;
                const kw = useAppt ? keywordMatch!.keyword : providerGroupKeywordMatch!.keyword;
                const color = useAppt ? Colors.success : Colors.primary;
                return (
                  <>
                    {name.slice(0, idx)}
                    <Box component="span" sx={{ color, fontWeight: 600 }}>
                      {name.slice(idx, idx + kw.length)}
                    </Box>
                    {name.slice(idx + kw.length)}
                  </>
                );
              })()}
            </Typography>
            <Typography variant="caption" sx={{ color: keywordMatch ? Colors.success : "text.disabled" }}>
              {keywordMatch ? (
                <>
                  Appointment type keyword detected:{" "}
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    {keywordMatch.keyword}
                  </Box>
                  {" • Assigned to "}
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    {keywordMatch.appointmentType.label || keywordMatch.appointmentType.name}
                  </Box>
                </>
              ) : (
                "No appointment type keyword detected"
              )}
            </Typography>
            {providerGroupKeywordMatch && (
              <Typography variant="caption" sx={{ color: Colors.primary }}>
                Provider group keyword detected:{" "}
                <Box component="span" sx={{ fontWeight: 600 }}>
                  {providerGroupKeywordMatch.keyword}
                </Box>
                {" • Assigned to "}
                <Box component="span" sx={{ fontWeight: 600 }}>
                  {providerGroupKeywordMatch.providerGroup.name}
                </Box>
              </Typography>
            )}
          </Box>
        )}
      </TableCell>
      <TableCell>
        {patient.loading ? (
          <Typography variant="body2" color="text.secondary">
            Loading...
          </Typography>
        ) : patient.numPerWeek === 0 ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, color: Colors.error }}>
              Plan of care not set
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Frequency missing in WebPT
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {`${patient.rawFrequency}`}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, color: "text.primary" }}>
              {`${patient.numPerWeek}x / week \u2022 ${patient.numWeeks} wks left`}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {`Scheduled ${patient.numVisits} of ${patient.totalPlanOfCareVisits} visits`}
            </Typography>
          </Box>
        )}
      </TableCell>
      <TableCell>
        {patient.loading ? (
          <Typography variant="body2" color="text.secondary">
            Loading...
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {patient.insuranceName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {`${patient.usedAuthorizedVisits}/${patient.authorizedVisits} visits used`}
              {` \u2022 ${patient.remainingAuthorizedVisits} left`}
            </Typography>
            {patient.expirationDate !== "N/A" && (
              <Typography
                variant="caption"
                sx={{
                  color: authorizationExpired ? Colors.error : authorizationExpiring ? Colors.warning : "text.secondary",
                }}
              >
                {authorizationExpired ? "Expired" : "Expires"} {patient.expirationDate}
              </Typography>
            )}
            {patient.ignoreAuthorization && (
              <Typography variant="caption" color="text.secondary">
                Authorization bypassed for this payer
              </Typography>
            )}
          </Box>
        )}
      </TableCell>
      <TableCell align="center">{patient.loading ? <span>Loading...</span> : `${patient.numBookable}`}</TableCell>
      <TableCell align="center">{patient.loading ? <span>Loading...</span> : `$${patient.potentialRevenue}`}</TableCell>
      <TableCell align="right">
        {planOfCare ? (
          <Button size="small" variant="text" onClick={() => onViewPlanOfCare(patient.id)} sx={{ fontWeight: 500 }}>
            View plan of care
          </Button>
        ) : planOfCareLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <CircularProgress size={18} />
          </Box>
        ) : (
          <Button size="small" variant="outlined" onClick={() => fetchPlanOfCare(patient.id)} style={{ color: Colors.primary, borderColor: Colors.primary }}>
            Fetch plan of care
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
};

const AutoAdd: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const organization = useSelector((state: RootState) => state.auth.organization);
  const providerGroups = useSelector((state: RootState) => state.providers.providerGroups);

  const patientMap = useSelector((state: RootState) => state.patients.patientMap);
  const existingIds = new Set(Object.values(patientMap).map((patient) => patient[EHR.webpt]?.webptPatientId || 0));

  const [basePatientData, setBasePatientData] = React.useState<{ id: number; name: string; insuranceId: number }[]>([]);
  const [planOfCareMetadataMap, setPlanOfCareMetadataMap] = React.useState<Map<number, WebptPlanOfCareMetadata>>(new Map());
  const [insurances, setInsurances] = React.useState<Insurance[]>([]);

  React.useEffect(() => {
    const fetchInsurances = async () => {
      if (!user?.token) return;
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/payers`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        setInsurances(response.data || []);
      } catch (error) {
        console.error("Error fetching insurances:", error);
      }
    };
    if (user?.token) {
      fetchInsurances();
    }
  }, [user?.token]);

  const patientData = basePatientData.map((patient) => {
    const patientMetadata = planOfCareMetadataMap.get(patient.id);
    const insurance = insurances.find((ins) => ins.ehrInsuranceId === patient.insuranceId.toString());
    const ignoreAuthorization = insurance?.byPassAuthorization || false;
    const insuranceName = insurance?.name || "Unknown Insurance";

    let remainingAuthorizedWeeks = 0;
    if (patientMetadata && patientMetadata.expirationDate) {
      const expirationDate = DateTime.fromFormat(patientMetadata.expirationDate, "yyyy-MM-dd");
      const now = DateTime.local();
      const weeksDiff = expirationDate.diff(now, "weeks").weeks;
      remainingAuthorizedWeeks = Math.max(0, Math.ceil(isNaN(weeksDiff) ? 0 : weeksDiff));
    }
    const visitsBeforeExpiration = remainingAuthorizedWeeks * (patientMetadata?.numPerWeek || 0);

    const remainingAuthorizedVisits = patientMetadata
      ? Math.max(0, (patientMetadata.totalAuthorizedVisits || 0) - (patientMetadata.usedAuthorizedVisits || 0) - (patientMetadata.numFutureScheduledVisits || 0))
      : 0;
    const totalPlanOfCareVisits = patientMetadata ? (patientMetadata.numWeeks || 0) * (patientMetadata.numPerWeek || 0) : 0;
    const remainingPlanOfCareVisits = Math.max(0, totalPlanOfCareVisits - (patientMetadata ? patientMetadata.numVisits || 0 : 0));

    const numBookable = ignoreAuthorization ? Math.max(remainingPlanOfCareVisits, 0) : Math.max(Math.min(remainingAuthorizedVisits, remainingPlanOfCareVisits, visitsBeforeExpiration), 0);
    const potentialRevenue = isNaN(numBookable) ? 0 : numBookable * 100; // Assuming $100 per visit

    return {
      id: patient.id,
      name: patient.name,
      caseStudyName: patientMetadata ? patientMetadata.caseStudyName : "N/A",
      insuranceName: insuranceName,
      loading: patientMetadata ? false : true,
      numWeeks: Math.max(patientMetadata?.numWeeks || 0, 0),
      numPerWeek: Math.max(patientMetadata?.numPerWeek || 0, 0),
      numVisits: Math.max(patientMetadata?.numVisits || 0, 0),
      rawFrequency: patientMetadata?.rawFrequency || "unknown",
      authorizedVisits: Math.max(patientMetadata?.totalAuthorizedVisits || 0, 0),
      usedAuthorizedVisits: Math.max((patientMetadata?.usedAuthorizedVisits || 0) + (patientMetadata?.numFutureScheduledVisits || 0), 0),
      expirationDate: patientMetadata ? patientMetadata.expirationDate : "N/A",

      remainingAuthorizedVisits: Math.max(Math.min(visitsBeforeExpiration, remainingAuthorizedVisits), 0),
      remainingPlanOfCareVisits: Math.max(remainingPlanOfCareVisits, 0),
      totalPlanOfCareVisits: Math.max(totalPlanOfCareVisits, 0),

      numBookable: Math.max(numBookable, 0),
      potentialRevenue: Math.max(potentialRevenue, 0),

      ignoreAuthorization: ignoreAuthorization || false,
    };
  });
  const anyLoading = patientData.some((patient) => patient.loading);
  const numPatients = patientData.length;
  const numLoadedPatients = patientData.filter((patient) => !patient.loading).length;
  const loadingPatientData = patientData.filter((patient) => patient.loading);
  const validPatientData = patientData.filter((patient) => !patient.loading && patient.numPerWeek > 0);
  const invalidPatientData = patientData.filter((patient) => !patient.loading && patient.numPerWeek === 0);

  const [planOfCaresMap, setPlanOfCaresMap] = React.useState<Map<number, CompleteActivePlanOfCare | null>>(new Map());
  const [appointmentTypes, setAppointmentTypes] = React.useState<AppointmentType[]>([]);

  const dispatch = useAppDispatch();
  const { showMessage } = useSnackbar();
  const navigate = useNavigate();

  const [queue, setQueue] = React.useState<QueuePatient[]>([]);
  const queueIds = new Set(queue.filter((item) => !item.done).map((item) => item.webptPatientId));

  const [initialLoading, setInitialLoading] = React.useState<boolean>(false);
  const [reconnectKey, setReconnectKey] = React.useState(0);

  React.useEffect(() => {
    const fetchQueue = async () => {
      if (!user?.token) return;
      try {
        const response = await api.get(`/patients/queue`, user.token);
        setQueue(response.data || []);
      } catch (error) {
        console.error("Error fetching queue:", error);
        setQueue([]);
      }
    };
    fetchQueue();
  }, [user?.token]);

  const [planOfCareLoadingIds, setPlanOfCareLoadingIds] = React.useState<Set<number>>(new Set());

  const [planOfCareDialogOpen, setPlanOfCareDialogOpen] = React.useState<boolean>(false);
  const [selectedPlanOfCarePatientId, setSelectedPlanOfCarePatientId] = React.useState<number | null>(null);

  React.useEffect(() => {
    if (user?.token) {
      dispatch(getWaitlistPatients({ token: user.token }));
    }
  }, [user?.token]);

  React.useEffect(() => {
    const fetchAppointmentTypes = async () => {
      if (!user?.token) return;
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/appointment-types`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        setAppointmentTypes(response.data || []);
      } catch (error) {
        console.error("Error fetching appointment types:", error);
      }
    };

    if (user?.token) {
      fetchAppointmentTypes();
    }
  }, [user?.token]);

  const fetchPlanOfCare = async (id: number) => {
    if (!user?.token) return;
    setPlanOfCareLoadingIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/plan-of-care/auto-generated-plan-of-care?webptPatientId=${id}`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      const planOfCare: CompleteActivePlanOfCare | null = response.data || null;
      setPlanOfCaresMap((prev) => new Map(prev).set(id, planOfCare));
    } catch (error) {
      console.error("Error fetching plan of care:", error);
    } finally {
      setPlanOfCareLoadingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const resetCache = async () => {
    if (!user?.token) return;
    await api.get(`/webpt/patients-from-schedule/refresh`, user.token);
    if (socketRef.current) {
      socketRef.current.close();
      socketRef.current = null;
    }
    setBasePatientData([]);
    setPlanOfCareMetadataMap(new Map());
    setReconnectKey((k) => k + 1);
  };

  const socketRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    if (!user || !user?.userId || !user?.token) {
      return;
    }

    // Check if a WebSocket connection already exists
    if (socketRef.current && (socketRef.current.readyState === WebSocket.OPEN || socketRef.current.readyState === WebSocket.CONNECTING)) {
      console.log("WebSocket connection is already open or connecting.");
      return;
    }

    const connectWebSocket = async () => {
      try {
        if (!user?.token) {
          return;
        }
        setInitialLoading(true);
        const url =
          process.env.REACT_APP_ENV === "development"
            ? `ws://${process.env.REACT_APP_BACKEND_URL_NO_HTTPS}/api/webpt/ws/patients-from-schedule?token=${encodeURIComponent(user.token)}`
            : `wss://${process.env.REACT_APP_BACKEND_URL_NO_HTTPS}/api/webpt/ws/patients-from-schedule?token=${encodeURIComponent(user.token)}`;
        const socket = new WebSocket(url);
        socketRef.current = socket;

        socket.onopen = async () => {
          // Add a delay to ensure the WebSocket connection is ready before subscribing
          // TODO (Hizami) - Figure out why the websocket connection is not immediately ready
          await new Promise((resolve) => setTimeout(resolve, 1000));
          console.log("Connected to WebSocket");
        };

        socket.onmessage = (event: MessageEvent<string>) => {
          const data = JSON.parse(event.data);
          switch (data.type) {
            case "patients":
              setBasePatientData(data.data);
              setInitialLoading(false);
              break;
            case "patientData":
              const patientId = data.data.id;
              setPlanOfCareMetadataMap((prev) => {
                const newMap = new Map(prev);
                newMap.set(patientId, data.data);
                return newMap;
              });
              break;
            default:
              console.warn("Unknown message type:", data.type);
              break;
          }
        };

        socket.onclose = () => {
          console.log("Disconnected from WebSocket");
          socketRef.current = null;
          setInitialLoading(false);
        };

        socket.onerror = (error) => {
          console.error("WebSocket error:", error);
          socket.close();
        };

        return () => {
          socket.close();
        };
      } catch (error) {
        console.error("Error connecting to WebSocket:", error);
        setInitialLoading(false);
      }
    };

    connectWebSocket();

    // Clean up the WebSocket connection when the component unmounts or user changes
    return () => {
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }
    };
  }, [user?.token, reconnectKey]);

  const quickAddAll = async () => {
    if (!user?.token) return;
    const patients = validPatientData.filter((patient) => !existingIds.has(patient.id) && !queueIds.has(patient.id));
    try {
      api.post(`/patients/webpt/quick-add-all`, user.token, {
        webptPatients: patients.map((p) => ({ id: p.id, name: p.name })),
      });

      showMessage(`${patients.length} patients added successfully`, "success");
      navigate("/dashboard/patients/auto-add-queue");
    } catch (error) {
      console.error("Error quick adding patients:", error);
      showMessage("Error quick adding patients", "error");
    } finally {
    }
  };

  const [confirmingQuickAddAll, setConfirmingQuickAddAll] = React.useState(false);

  const [view, setView] = React.useState<"loading" | "valid" | "invalid">("valid");
  const visibleLoadingPatients = loadingPatientData.filter((patient) => !existingIds.has(patient.id) && !queueIds.has(patient.id));
  const visibleValidPatients = validPatientData.filter((patient) => !existingIds.has(patient.id) && !queueIds.has(patient.id)).sort((a, b) => b.potentialRevenue - a.potentialRevenue);
  const visibleInvalidPatients = invalidPatientData.filter((patient) => !existingIds.has(patient.id) && !queueIds.has(patient.id)).sort((a, b) => b.potentialRevenue - a.potentialRevenue);

  const exportPatients = view === "invalid" ? visibleInvalidPatients : visibleValidPatients;

  const exportToCsv = () => {
    const rows = exportPatients.map((patient) => {
      const keywordMatch = findAppointmentTypeKeywordMatch(patient.caseStudyName, appointmentTypes);
      const providerGroupKeywordMatch = findProviderGroupKeywordMatch(patient.caseStudyName, providerGroups);
      return {
        "Patient Name": patient.name,
        "Patient ID": patient.id,
        "WebPT URL": `https://app.webpt.com/patient/edit/index/PatientID/${patient.id}`,
        "Case Study": patient.caseStudyName,
        "Appointment Type Keyword": keywordMatch?.keyword || "",
        "Appointment Type": keywordMatch ? keywordMatch.appointmentType.label || keywordMatch.appointmentType.name : "",
        "Provider Group Keyword": providerGroupKeywordMatch?.keyword || "",
        "Provider Group": providerGroupKeywordMatch?.providerGroup.name || "",
        "Plan of Care": patient.numPerWeek === 0 ? `Not set (${patient.rawFrequency})` : `${patient.numPerWeek}x / week, ${patient.numWeeks} wks left`,
        "Scheduled Visits": patient.numVisits,
        "Total Plan of Care Visits": patient.totalPlanOfCareVisits,
        Insurance: patient.insuranceName,
        "Authorized Visits Used": patient.usedAuthorizedVisits,
        "Total Authorized Visits": patient.authorizedVisits,
        "Remaining Authorized Visits": patient.remainingAuthorizedVisits,
        "Authorization Expiration": patient.expirationDate,
        "Authorization Bypassed": patient.ignoreAuthorization ? "Yes" : "No",
        "Bookable Visits": patient.numBookable,
        "Potential Revenue": patient.potentialRevenue,
      };
    });
    if (rows.length === 0) return;
    const parser = new Parser();
    const csv = parser.parse(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `recommended_patients_${view === "invalid" ? "missing_plan_of_care" : "add_able"}_${DateTime.local().toFormat("yyyy-MM-dd")}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleOpenPlanOfCareDialog = async (patientId: number) => {
    if (!planOfCaresMap.get(patientId)) {
      await fetchPlanOfCare(patientId);
    }
    setSelectedPlanOfCarePatientId(patientId);
    setPlanOfCareDialogOpen(true);
  };

  const selectedPlanOfCare = selectedPlanOfCarePatientId !== null ? planOfCaresMap.get(selectedPlanOfCarePatientId) || null : null;

  const selectedPatient = selectedPlanOfCarePatientId !== null ? patientData.find((p) => p.id === selectedPlanOfCarePatientId) || null : null;

  return (
    <Box>
      <SignoutHeader />
      <Box
        sx={{
          maxWidth: 1200,
          mx: "auto",
          px: 3,
          py: 3,
        }}
      >
        <Box sx={{ mb: 2 }}>
          <Link to="/dashboard/patients" style={{ textDecoration: "none" }}>
            <SecondaryButton
              sx={{
                display: { xs: "none", lg: "flex" },
                alignItems: "center",
                mb: 1,
              }}
            >
              <ArrowBackIcon sx={{ mr: 1 }} /> Back
            </SecondaryButton>
          </Link>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 3,
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography variant="h5" sx={{ mb: 0.5 }}>
              Recommended patients
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Review and add patients who have bookable visits based on their plan of care.
            </Typography>
          </Box>

          <Box
            sx={{
              minWidth: 220,
              textAlign: "right",
            }}
          >
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{
                letterSpacing: 0.08,
              }}
            >
              Total bookable revenue
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "flex-end",
                gap: 1,
              }}
            >
              <Typography variant="h4" sx={{ fontWeight: 600, lineHeight: 1 }}>
                $
                {patientData.reduce((total, patient) => {
                  if (!existingIds.has(patient.id) && !queueIds.has(patient.id)) {
                    const revenue = isNaN(patient.potentialRevenue) ? 0 : patient.potentialRevenue;
                    return total + revenue;
                  }
                  return total;
                }, 0)}
              </Typography>
              {anyLoading && <CircularProgress size={16} />}
            </Box>
            {anyLoading && numPatients > 0 && (
              <Typography variant="caption" color="text.secondary">
                Loading {numLoadedPatients} / {numPatients} patients
              </Typography>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2.5,
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Tabs
            value={view}
            onChange={(_, newValue) => setView(newValue)}
            textColor="primary"
            indicatorColor="primary"
            sx={{
              minHeight: 36,
              "& .MuiTab-root": {
                textTransform: "none",
                minHeight: 36,
              },
            }}
          >
            <Tab value="valid" label={`Add-able (${visibleValidPatients.length})`} />
            <Tab value="loading" label={`Loading (${visibleLoadingPatients.length})`} />
            <Tab value="invalid" label={`Missing plan of care (${visibleInvalidPatients.length})`} />
          </Tabs>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {process.env.REACT_APP_ENV === "development" && (
              <Button variant="outlined" color="primary" sx={{ textTransform: "none" }} onClick={resetCache} startIcon={<RefreshIcon fontSize="small" />}>
                Refresh
              </Button>
            )}
            {view !== "loading" && (
              <Button
                variant="outlined"
                color="primary"
                sx={{ textTransform: "none" }}
                disabled={initialLoading || anyLoading || exportPatients.length === 0}
                onClick={exportToCsv}
                startIcon={<FileDownloadOutlined fontSize="small" />}
              >
                Export CSV
              </Button>
            )}
            <Button variant="outlined" color="primary" sx={{ textTransform: "none" }} component={Link} to="/dashboard/patients/auto-add-queue" startIcon={<ListAltOutlinedIcon fontSize="small" />}>
              View queue
            </Button>
            {view === "valid" && (
              <Button
                variant="contained"
                color="primary"
                disabled={visibleValidPatients.length === 0}
                onClick={() => setConfirmingQuickAddAll(true)}
                sx={{ textTransform: "none" }}
                startIcon={<PlaylistAddIcon fontSize="small" />}
              >
                Quick add all
              </Button>
            )}
          </Box>
        </Box>

        {view === "invalid" && (
          <Typography variant="body2" color={Colors.error} sx={{ mb: 2, maxWidth: 600 }}>
            We were unable to calculate the plan of care for these patients. This may be because the plan of care does not exist or the frequency is in an unrecognized format.
          </Typography>
        )}

        <Paper
          variant="outlined"
          sx={{
            borderRadius: 2,
            boxShadow: "none",
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          <TableContainer>
            <Table size="small" sx={{ "& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root": { borderBottom: 0 } }}>
              <TableHead>
                <TableRow>
                  <TableCell>Action</TableCell>
                  <TableCell>Patient</TableCell>
                  <TableCell>Case Study</TableCell>
                  <TableCell>Plan of care</TableCell>
                  <TableCell>Authorization</TableCell>
                  <TableCell align="center">Bookable visits</TableCell>
                  <TableCell align="center">Potential revenue</TableCell>
                  <TableCell align="right">Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {initialLoading && patientData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Box
                        sx={{
                          py: 4,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 2,
                        }}
                      >
                        <CircularProgress size={24} />
                        <Typography variant="body2" color="text.secondary">
                          Loading recommended patients from your schedule…
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {view === "valid" &&
                      (visibleValidPatients.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center">
                            <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                              {anyLoading ? "Loading patients from your schedule…" : "No add-able patients found."}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        visibleValidPatients.map((patient) => (
                          <AutoAddRow
                            key={patient.id}
                            patient={patient}
                            fetchPlanOfCare={fetchPlanOfCare}
                            planOfCare={planOfCaresMap.get(patient.id) || null}
                            appointmentTypes={appointmentTypes}
                            providerGroups={providerGroups}
                            planOfCareLoading={planOfCareLoadingIds.has(patient.id)}
                            onViewPlanOfCare={handleOpenPlanOfCareDialog}
                          />
                        ))
                      ))}

                    {view === "loading" &&
                      (visibleLoadingPatients.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center">
                            <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                              No patients currently loading.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        visibleLoadingPatients.map((patient) => (
                          <AutoAddRow
                            key={patient.id}
                            patient={patient}
                            fetchPlanOfCare={fetchPlanOfCare}
                            planOfCare={planOfCaresMap.get(patient.id) || null}
                            appointmentTypes={appointmentTypes}
                            providerGroups={providerGroups}
                            planOfCareLoading={planOfCareLoadingIds.has(patient.id)}
                            onViewPlanOfCare={handleOpenPlanOfCareDialog}
                          />
                        ))
                      ))}

                    {view === "invalid" &&
                      (visibleInvalidPatients.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center">
                            <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                              {anyLoading ? "Loading patients from your schedule…" : "No patients with missing plans of care."}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        visibleInvalidPatients.map((patient) => (
                          <AutoAddRow
                            key={patient.id}
                            patient={patient}
                            fetchPlanOfCare={fetchPlanOfCare}
                            planOfCare={planOfCaresMap.get(patient.id) || null}
                            appointmentTypes={appointmentTypes}
                            providerGroups={providerGroups}
                            planOfCareLoading={planOfCareLoadingIds.has(patient.id)}
                            onViewPlanOfCare={handleOpenPlanOfCareDialog}
                          />
                        ))
                      ))}
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      <Dialog open={confirmingQuickAddAll} onClose={() => setConfirmingQuickAddAll(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Quick add all patients?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This will add all eligible patients to the auto-add queue. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmingQuickAddAll(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setConfirmingQuickAddAll(false);
              quickAddAll();
            }}
          >
            Yes, quick add all
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={planOfCareDialogOpen}
        onClose={() => setPlanOfCareDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: Colors.bg,
          },
        }}
      >
        <DialogTitle>
          Plan of care
          {selectedPatient && (
            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 0.5 }}>
              {selectedPatient.name} • ID {selectedPatient.id}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ p: 2.5 }}>
          {!selectedPlanOfCare || appointmentTypes.length === 0 ? (
            <Box
              sx={{
                py: 4,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <PlanOfCareActivePreview activePlanOfCare={selectedPlanOfCare} appointmentTypes={appointmentTypes} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlanOfCareDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AutoAdd;
